import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./UpdateLicense.css";

const UpdateLicense = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialLicense = location.state?.license;

  const [formData, setFormData] = useState({
    id: "",
    customer_name: "",
    network_name: "",
    machine_uuid: "",
    license_type: "monthly",
    channel_limit: 0,
    max_concurrent_viewers: 0,
    license_status: "trial",
    expiry_date: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (initialLicense) {
      let formattedDate = "";
      if (initialLicense.expiry_date) {
        const d = new Date(initialLicense.expiry_date);
        formattedDate = d.toISOString().split("T")[0];
      }

      setFormData({
        id: initialLicense.id || "",
        customer_name: initialLicense.customer_name || "N/A",
        network_name: initialLicense.network_name || "N/A",
        machine_uuid: initialLicense.machine_uuid || "",
        license_type: initialLicense.license_type || "monthly",
        channel_limit: initialLicense.channel_limit ?? 0,
        max_concurrent_viewers: initialLicense.max_concurrent_viewers ?? 0,
        license_status: initialLicense.license_status || "trial",
        expiry_date: formattedDate,
      });
    } else {
      navigate("/manage-license");
    }
  }, [initialLicense, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // কুইক রিনিউ (কারেন্ট ডেট থেকে N দিন যোগ, status auto active)
  const handleQuickRenew = (days) => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + days);
    setFormData((prev) => ({
      ...prev,
      expiry_date: targetDate.toISOString().split("T")[0],
      license_status: "active",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await axios.post("/api/licenses/update", formData);
      if (res.data?.status === "success") {
        setSuccess("License updated successfully!");
        setTimeout(() => {
          navigate("/manage-license");
        }, 1200);
      } else {
        setError(res.data?.message || "Failed to update license.");
      }
    } catch (err) {
      console.error("Error updating license engine:", err);
      setError(err.response?.data?.message || "Internal connection fault to subscription database.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="update-license-container">
      <div className="update-license-card-shell">

        {/* HEADER */}
        <div className="update-license-header">
          <h2>Renew & Update License</h2>
          <p>Modifying parameters for Client ID: <strong>#{formData.id}</strong></p>
        </div>

        {error && <div className="license-alert-error">{error}</div>}
        {success && <div className="license-alert-success">{success}</div>}

        <form onSubmit={handleSubmit} className="update-license-form">

          {/* READ-ONLY INFO */}
          <div className="form-readonly-group-grid">
            <div className="readonly-box">
              <label>Customer Name</label>
              <p>{formData.customer_name}</p>
            </div>
            <div className="readonly-box">
              <label>Network / Company</label>
              <p>{formData.network_name}</p>
            </div>
          </div>

          <div className="form-group full-width">
            <label>Machine Hardware UUID (HWID Lock)</label>
            <input
              type="text"
              value={formData.machine_uuid || "Pending enrollment..."}
              readOnly
              className="readonly-input-field"
            />
          </div>

          <hr className="form-divider" />

          {/* EDITABLE PARAMETERS */}
          <div className="form-input-group-grid">

            {/* PLAN TYPE */}
            <div className="form-group">
              <label htmlFor="license_type">Subscription Plan Type</label>
              <select
                id="license_type"
                name="license_type"
                value={formData.license_type}
                onChange={handleChange}
              >
                <option value="monthly">Monthly Plan</option>
                <option value="lifetime">Lifetime Plan</option>
                <option value="others">Others Plan</option>
              </select>
            </div>

            {/* CHANNEL LIMIT */}
            <div className="form-group">
              <label htmlFor="channel_limit">Max Channel / Stream Limit</label>
              <input
                id="channel_limit"
                type="number"
                name="channel_limit"
                value={formData.channel_limit}
                onChange={handleChange}
                min="0"
                placeholder="0 for Unlimited"
              />
            </div>

            {/* MAX CONCURRENT VIEWERS */}
            <div className="form-group">
              <label htmlFor="max_concurrent_viewers">Max Concurrent Viewers</label>
              <input
                id="max_concurrent_viewers"
                type="number"
                name="max_concurrent_viewers"
                value={formData.max_concurrent_viewers}
                onChange={handleChange}
                min="0"
                placeholder="0 for Unlimited"
              />
            </div>

            {/* LICENSE STATUS */}
            <div className="form-group">
              <label htmlFor="license_status">License Status</label>
              <select
                id="license_status"
                name="license_status"
                value={formData.license_status}
                onChange={handleChange}
              >
                <option value="active">ACTIVE</option>
                <option value="trial">TRIAL</option>
                <option value="expired">EXPIRED</option>
                <option value="suspended">SUSPENDED</option>
              </select>
            </div>

            {/* EXPIRY DATE */}
            <div className="form-group">
              <label htmlFor="expiry_date">Expiration Date</label>
              <input
                id="expiry_date"
                type="date"
                name="expiry_date"
                value={formData.expiry_date}
                onChange={handleChange}
                disabled={formData.license_type === "lifetime"}
              />
            </div>

          </div>

          {/* QUICK RENEW */}
          {formData.license_type !== "lifetime" && (
            <div className="quick-renew-section">
              <span className="quick-renew-label">Quick Extensions:</span>
              <div className="quick-renew-actions">
                <button type="button" onClick={() => handleQuickRenew(3)}>+3 Days Trial</button>
                <button type="button" onClick={() => handleQuickRenew(30)}>+1 Month</button>
                <button type="button" onClick={() => handleQuickRenew(180)}>+6 Months</button>
                <button type="button" onClick={() => handleQuickRenew(365)}>+1 Year</button>
              </div>
            </div>
          )}

          {/* FOOTER */}
          <div className="update-license-footer-buttons">
            <button
              type="button"
              className="btn-cancel-matrix"
              onClick={() => navigate("/manage-license")}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit-matrix"
              disabled={loading}
            >
              {loading ? "Committing..." : "Commit Update"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default UpdateLicense;